import { createContext, forwardRef, useContext, type ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Table,
  TableCell,
  TableContainer,
  type TableCellProps,
  type TableContainerProps,
  type TableProps,
} from './Table';
import { mergeSx } from '../utils/mergeSx';
import { table as tableTokens } from '@/shared/theme/tokens';

export type EditableTableDensity = 'dense' | 'comfortable';

export interface EditableTableContainerProps extends TableContainerProps {
  tableProps?: TableProps;
  /**
   * `dense` — spreadsheet grid (line-item editors).
   * `comfortable` — list-table padding and row dividers only.
   */
  density?: EditableTableDensity;
  children: ReactNode;
}

const EDITABLE_CELL_CLASS = 'editable-table-cell';

const EditableTableDensityContext = createContext<EditableTableDensity>('dense');

const denseContainerSx: SxProps<Theme> = {
  flex: 'none',
  minHeight: 'auto',
  height: 'auto',
  maxHeight: 'none',
  overflow: 'visible',
  boxShadow: 'none',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};

const comfortableContainerSx: SxProps<Theme> = {
  flex: 'none',
  minHeight: 'auto',
  height: 'auto',
  maxHeight: 'none',
  overflow: 'auto',
};

const denseTableSx: SxProps<Theme> = {
  borderCollapse: 'collapse',
  // '&&&' bumps specificity above shared TableCell defaults
  // (which use ':not(.MuiTableCell-head):not(.MuiTableCell-footer)')
  '&&& th, &&& td': {
    border: 1,
    borderColor: 'divider',
  },
  [`&&& th, &&& td:not(.${EDITABLE_CELL_CLASS})`]: {
    px: 0.75,
    py: 0.25,
  },
  '&&& th': {
    bgcolor: 'action.hover',
    fontWeight: 600,
    height: 'auto',
  },
  [`&&& td.${EDITABLE_CELL_CLASS}`]: {
    p: 0,
  },
};

/**
 * Visual shell for inline-edit / line-item tables.
 * Callers still compose TableHead / TableBody / TableRow themselves.
 */
export const EditableTableContainer = forwardRef<HTMLDivElement, EditableTableContainerProps>(
  function EditableTableContainer(
    {
      tableProps,
      density = 'dense',
      sx,
      children,
      ...props
    },
    ref,
  ) {
  const { sx: tableSx, stickyHeader = false, size = 'small', ...restTableProps } = tableProps ?? {};

  return (
    <EditableTableDensityContext.Provider value={density}>
      <TableContainer
        ref={ref}
        sx={mergeSx(density === 'comfortable' ? comfortableContainerSx : denseContainerSx, sx)}
        {...props}
      >
        <Table
          size={size}
          stickyHeader={stickyHeader}
          sx={mergeSx(density === 'dense' ? denseTableSx : undefined, tableSx)}
          {...restTableProps}
        >
          {children}
        </Table>
      </TableContainer>
    </EditableTableDensityContext.Provider>
  );
});

export interface EditableTableCellProps extends TableCellProps {
  /** Highlights the cell outline (e.g. validation error). */
  error?: boolean;
  /** Text alignment for descendant InputBase / TextField / Select inputs. */
  inputAlign?: 'left' | 'right' | 'center';
}

const editableCellBaseSx: SxProps<Theme> = {
  '&:hover': {
    bgcolor: 'action.selected',
  },
  '&:focus-within': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
    bgcolor: 'background.paper',
  },
  '& .MuiSelect-icon': {
    opacity: 0,
    color: 'text.secondary',
    transition: (theme) => theme.transitions.create('opacity', { duration: 120 }),
  },
  '&:hover .MuiSelect-icon, &:focus-within .MuiSelect-icon': {
    opacity: 1,
  },
  // Autocomplete clear/popup icons — same quiet-until-hover pattern as Select.
  '& .MuiAutocomplete-endAdornment': {
    opacity: 0,
    transition: (theme) => theme.transitions.create('opacity', { duration: 120 }),
  },
  '&:hover .MuiAutocomplete-endAdornment, &:focus-within .MuiAutocomplete-endAdornment': {
    opacity: 1,
  },
  // InputBase (flush)
  '& .MuiInputBase-root:not(.MuiOutlinedInput-root)': {
    px: 0.75,
    py: 0.25,
    fontSize: '0.875rem',
    width: '100%',
  },
  '& .MuiInputBase-root:not(.MuiOutlinedInput-root) .MuiInputBase-input': {
    p: 0,
  },
  // Outlined TextField / Select / DatePicker (borderless inside cell).
  // `&&&` beats `FormControl`'s `outlinedFieldSx`, which paints `background.paper`.
  '&&& .MuiOutlinedInput-root, &&& .MuiPickersOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: 'transparent',
    minWidth: 0,
  },
  '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
    border: 'none',
  },
  // Focus ring lives on the cell (`:focus-within`); kill the input's own focused border.
  '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, && .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-root.Mui-focused, & .MuiPickersOutlinedInput-root.Mui-focused': {
    boxShadow: 'none',
  },
};

const denseCellSx: SxProps<Theme> = {
  p: 0,
};

/**
 * Keep list-table cell padding; the control must not add `form.controlHeight` on top.
 * `&&&` beats the theme pin (36px) and Autocomplete `minHeight: 36`.
 */
const comfortableCellSx: SxProps<Theme> = {
  // Row hover owns the wash — no darker per-cell highlight.
  '&:hover': {
    bgcolor: 'transparent',
  },
  '&:focus-within': {
    bgcolor: 'transparent',
  },
  '&&& .MuiOutlinedInput-root, &&& .MuiPickersOutlinedInput-root, &&& .MuiPickersInputBase-root, &&& .MuiAutocomplete-inputRoot': {
    height: 'auto',
    minHeight: 0,
    padding: 0,
    fontSize: 'inherit',
    lineHeight: tableTokens.headerLineHeight,
  },
  '&&& .MuiOutlinedInput-input, &&& .MuiSelect-select, &&& .MuiPickersSectionList-root, &&& .MuiAutocomplete-input': {
    padding: 0,
    height: 'auto',
    minHeight: 0,
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  '&&& .MuiPickersInputBase-sectionsContainer, &&& .MuiPickersOutlinedInput-sectionsContainer': {
    padding: '0 !important',
    margin: 0,
  },
  '&&& .MuiAutocomplete-input': {
    padding: '0 !important',
    minWidth: '0 !important',
    width: 'auto',
    flexGrow: 1,
  },
  '& .MuiInputAdornment-root, & .MuiPickersInputAdornment-root': {
    margin: 0,
    maxHeight: 'none',
  },
  '& .MuiPickersInputAdornment-root .MuiIconButton-root': {
    p: 0.25,
  },
};

/**
 * Zero-padding cell for inline controls.
 * Does not own value / onChange — callers pass their own TextField, Select, InputBase, etc.
 * Padding follows `EditableTableContainer` density.
 */
export const EditableTableCell = ({
  error = false,
  inputAlign = 'left',
  className,
  sx,
  children,
  ...props
}: EditableTableCellProps) => {
  const density = useContext(EditableTableDensityContext);

  return (
    <TableCell
      {...props}
      className={[EDITABLE_CELL_CLASS, className].filter(Boolean).join(' ')}
      sx={mergeSx(
        editableCellBaseSx,
        density === 'dense' ? denseCellSx : comfortableCellSx,
        {
          textAlign: inputAlign,
          '&:focus-within': {
            outlineColor: error ? 'error.main' : 'primary.main',
          },
          '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
            textAlign: inputAlign,
          },
        },
        sx,
      )}
    >
      {children}
    </TableCell>
  );
};
